import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  LinearProgress,
  Chip,
} from "@mui/material";
import { CheckCircle, Error } from "@mui/icons-material";
import { BulkStepProps, roadSourceIdsPayload } from "../../types";
import { PROJECT_SIZE_METERS } from "../../../../constants/project";
import { useAddProject } from "../../../../api/projects";
import { useNavigate } from "react-router-dom";
import { ProjectBoundary } from "../../../../types/project";

const bboxToPolygon = (top: number, left: number, bottom: number, right: number): ProjectBoundary => ({
  type: "Polygon",
  coordinates: [[[left, bottom], [right, bottom], [right, top], [left, top], [left, bottom]]],
});

const BulkReviewStep: React.FC<BulkStepProps> = ({
  data,
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [creationResults, setCreationResults] = useState<{
    success: number;
    failed: number;
    errors: string[];
  } | null>(null);

  const addProject = useAddProject();
  const navigate = useNavigate();

  const handleCreateProjects = async () => {
    setIsCreating(true);
    setCreationResults({ success: 0, failed: 0, errors: [] });

    const results = { success: 0, failed: 0, errors: [] as string[] };

    for (let i = 0; i < data.parsedProjects.length; i++) {
      const project = data.parsedProjects[i];

      try {
        // Calculate bounding box for this project
        const { x: bufferXInit, y: bufferYInit } = PROJECT_SIZE_METERS[data.projectSize];
        let bufferX = bufferXInit;
        let bufferY = bufferYInit;

        if (data.projectAspectRatio === "2:1") {
          bufferX *= 2;
        } else if (data.projectAspectRatio === "1:2") {
          bufferY *= 2;
        }

        const earthRadius = 6371000;
        const latBuffer = (bufferY / earthRadius) * (180 / Math.PI);
        const lngBuffer = (bufferX / earthRadius) * (180 / Math.PI) / Math.cos(project.lat * Math.PI / 180);

        const projectData = {
          name: project.name,
          description: `Bulk created project for ${project.name}`,
          folderId: data.folderId ?? null,
          vegetationSourceId: data.vegetationSource,
          fireHistorySourceId: data.fireHistorySource,
          location: bboxToPolygon(
            project.lat + latBuffer,
            project.lng - lngBuffer,
            project.lat - latBuffer,
            project.lng + lngBuffer,
          ),
          assetSourceIds: data.selectedAssetSources.map((source) => source.id),
          propertySourceIds: data.selectedPropertySources.map((source) => source.id),
          stampRoadsIntoVegetation: data.stampRoadsIntoVegetation,
          roadSourceIds: roadSourceIdsPayload(data),
          modelUrbanEnvironment: data.modelUrbanEnvironment,
          defaultConstructionTypeId:
            data.modelUrbanEnvironment && data.defaultConstructionTypeId !== ""
              ? data.defaultConstructionTypeId
              : null,
        };

        // Convert to FormData as expected by useAddProject
        const formData = new FormData();
        formData.append('data', JSON.stringify(projectData));

        await addProject.mutateAsync(formData);
        results.success++;
      } catch (error: unknown) {
        results.failed++;
        let errorMessage = 'Unknown error';
        if (error instanceof Error) {
          errorMessage = (error as Error).message;
        } else if (typeof error === 'object' && error !== null && 'message' in error) {
          errorMessage = String((error as { message: unknown }).message);
        } else if (typeof error === 'string') {
          errorMessage = error;
        }
        results.errors.push(`Failed to create project "${project.name}": ${errorMessage}`);
      }

      // Update progress
      setCreationResults({ ...results });
    }

    setIsCreating(false);

    // Redirect to projects listing page after creation (successful or with errors)
    if (results.success > 0) {
      setTimeout(() => {
        navigate(`/projects?refresh=true${data.folderId ? `&folder=${data.folderId}` : ""}`);
      }, 2000); // Give user time to see results
    }
  };

  const canProceed = () => {
    return (
      data.parsedProjects.length > 0 &&
      data.vegetationSource !== "" &&
      data.fireHistorySource !== ""
    );
  };

  return (
    <Box sx={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ p: 3, backgroundColor: "#f8f9fa", borderBottom: "1px solid #e0e0e0" }}>
        <Typography
          variant="h5"
          sx={{
            mb: 1,
            fontWeight: 600,
            color: "#1a1a1a",
          }}
        >
          Review & Create Projects
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#666",
          }}
        >
          Review your configuration and create {data.parsedProjects.length} projects
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, p: 3 }}>
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>

          {/* Configuration Summary */}
          <Card sx={{ mb: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Configuration Summary
              </Typography>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#666" }}>
                    PROJECT SETTINGS
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2">Projects to create:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{data.parsedProjects.length}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2">Size:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{data.projectSize}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2">Aspect Ratio:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{data.projectAspectRatio}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2">Dimensions:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {PROJECT_SIZE_METERS[data.projectSize].x}m × {PROJECT_SIZE_METERS[data.projectSize].y}m
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#666" }}>
                    DATA SOURCES
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2">Vegetation:</Typography>
                      <Chip
                        label={data.vegetationSource || "Not set"}
                        size="small"
                        color={data.vegetationSource ? "success" : "default"}
                        variant="outlined"
                      />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2">Fire History:</Typography>
                      <Chip
                        label={data.fireHistorySource || "Not set"}
                        size="small"
                        color={data.fireHistorySource ? "success" : "default"}
                        variant="outlined"
                      />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2">Asset Sources:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {data.selectedAssetSources.length} selected
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2">Property Boundaries:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {data.selectedPropertySources.length} selected
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Validation Alerts */}
          {!canProceed() && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Please ensure all required fields are completed:
                {!data.vegetationSource && " • Vegetation Source"}
                {!data.fireHistorySource && " • Fire History Source"}
                {data.parsedProjects.length === 0 && " • Upload CSV file with projects"}
              </Typography>
            </Alert>
          )}

          {/* Project List Preview */}
          <Card sx={{ mb: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Projects to Create ({data.parsedProjects.length})
              </Typography>

              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Project Name</TableCell>
                      <TableCell>Location</TableCell>
                      {Object.keys(data.parsedProjects[0] || {})
                        .filter(key => !['name', 'lat', 'lng'].includes(key))
                        .slice(0, 3)
                        .map(key => (
                          <TableCell key={key}>{key}</TableCell>
                        ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.parsedProjects.slice(0, 10).map((project, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ fontWeight: 500 }}>{project.name}</TableCell>
                        <TableCell>
                          {project.lat.toFixed(6)}, {project.lng.toFixed(6)}
                        </TableCell>
                        {Object.entries(project)
                          .filter(([key]) => !['name', 'lat', 'lng'].includes(key))
                          .slice(0, 3)
                          .map(([key, value]) => (
                            <TableCell key={key}>{String(value)}</TableCell>
                          ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {data.parsedProjects.length > 10 && (
                <Typography variant="caption" sx={{ mt: 2, color: "#666", display: "block" }}>
                  Showing first 10 projects. All {data.parsedProjects.length} projects will be created.
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Creation Progress */}
          {isCreating && (
            <Card sx={{ mb: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Creating Projects...
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={creationResults ? ((creationResults.success + creationResults.failed) / data.parsedProjects.length) * 100 : 0}
                  sx={{ mb: 2 }}
                />
                <Typography variant="body2" sx={{ color: "#666" }}>
                  {creationResults ? `${creationResults.success + creationResults.failed} of ${data.parsedProjects.length} projects processed` : "Starting..."}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Creation Results */}
          {creationResults && !isCreating && (
            <Card sx={{ mb: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  {creationResults.failed === 0 ? (
                    <CheckCircle sx={{ color: "#4caf50", mr: 1 }} />
                  ) : (
                    <Error sx={{ color: "#f44336", mr: 1 }} />
                  )}
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Creation Complete
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 4, mb: 2 }}>
                  <Box>
                    <Typography variant="h4" sx={{ color: "#4caf50", fontWeight: 600 }}>
                      {creationResults.success}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      Successful
                    </Typography>
                  </Box>
                  {creationResults.failed > 0 && (
                    <Box>
                      <Typography variant="h4" sx={{ color: "#f44336", fontWeight: 600 }}>
                        {creationResults.failed}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#666" }}>
                        Failed
                      </Typography>
                    </Box>
                  )}
                </Box>

                {creationResults.errors.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Errors:
                    </Typography>
                    {creationResults.errors.map((error, index) => (
                      <Alert key={index} severity="error" sx={{ mb: 1 }}>
                        {error}
                      </Alert>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Create Button */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleCreateProjects}
              disabled={!canProceed() || isCreating}
              sx={{
                backgroundColor: "#00d4ad",
                "&:hover": { backgroundColor: "#00b89c" },
                "&:disabled": { backgroundColor: "#e0e0e0", color: "#999" },
                px: 4,
                py: 1.5,
              }}
            >
              {isCreating ? "Creating Projects..." : `Create ${data.parsedProjects.length} Projects`}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BulkReviewStep;
